// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { AuthnMethod } from '@imprivata-cloud/authn';
import type { TraceContext } from '@imprivata-cloud/common/types';
import { addSpanAttributes } from '@imprivata-cloud/common';
import type { MemorySection } from './MemorySection';
import type { AuthenticationStatusCode } from '../../types';
import { closeWindow } from '../utils';
import { AgentEvent } from '../../constants';

const EPIC_CONNECTOR_TIMEOUT = 100;

export class AuthnSection {
  constructor(private readonly _memory: MemorySection) {}

  getDeviceFactorTypes(
    _traceContext: TraceContext,
    callback: (types: AuthnMethod[]) => void,
  ) {
    callback(Array.from(this._memory.connectedDevices.keys()));
  }

  getAppAccessContext(
    journeyId: string,
    _traceContext: TraceContext,
    callback: (appAccessContext: string) => void,
  ) {
    addSpanAttributes({
      get_app_access_context: {
        journeyId,
      },
    });
    callback(this._memory.appAccessContext);
  }

  submitCredentials2(
    journeyId: string,
    _traceContext: TraceContext,
    credentials: CredentialData,
  ) {
    addSpanAttributes({
      submit_credentials: {
        journeyId,
        credentials,
      },
    });
    this._memory.submittedCredentials.push(credentials);
  }

  setAuthZCode(
    journeyId: string,
    _traceContext: TraceContext,
    oidcAuthCode: string,
  ) {
    addSpanAttributes({
      set_oidc_auth_code: {
        journeyId,
        oidcAuthCode,
      },
    });
    this._memory.submittedOidcAuthCodes.push(oidcAuthCode);
  }

  endJourneyAndCloseWindow(
    journeyIdToClose: string,
    _traceContext: TraceContext,
  ) {
    addSpanAttributes({
      close_agent: { journeyIdToClose },
    });

    closeWindow();
  }

  setAuthenticationStatus(
    journeyId: string,
    traceContext: TraceContext,
    statusCode: AuthenticationStatusCode,
  ) {
    // At this moment the EA asks the Epic Conector whether it is ready to proceed.
    // Then it emits 'app-authentication-status' event with statusCode 'ready' or 'not-ready'
    // depending on the epic response.
    // Mock implementation emits just the same statusCode that was received from the UI
    addSpanAttributes({
      set_authentication_status: {
        journeyId,
        statusCode,
      },
    });

    const eventName = AgentEvent.APP_AUTHENTICATION_STATUS;
    const callback = this._memory.registeredCallbacks[eventName];

    if (callback) {
      setTimeout(() => {
        callback(eventName, {
          traceContext,
          statusData: {
            statusCode,
          },
        });
      }, EPIC_CONNECTOR_TIMEOUT);
    }
  }

  getPKCEData(
    workflowId: string,
    _traceContext: TraceContext,
    callback: (codeChallenge: string, codeChallengeMethod: string) => void,
  ) {
    addSpanAttributes({
      get_pkce_data: {
        workflowId,
      },
    });
    callback(this._memory.codeChallenge, this._memory.codeChallengeMethod);
  }

  getIdentityToken(
    workflowId: string,
    _traceContext: TraceContext,
    callback: (identityToken: string, codingContext: string) => void,
  ) {
    addSpanAttributes({
      get_identity_token: {
        workflowId,
      },
    });
    callback(this._memory.identityToken, this._memory.codingContext);
  }
}
